import { Injectable } from '@angular/core'
import { AbstractControl, FormBuilder, FormControl, FormGroup } from '@angular/forms'
import { ValidatorFunction } from '@widgets/validation'
import { dot } from 'dot-object'

@Injectable({
  providedIn: 'root',
})
export class FormHelperService {
  constructor(private fb: FormBuilder) {}

  createForm<T = any>(reference: T, data?: Partial<T>): FormGroup {
    const form = this.createFormGroup(reference)

    if (data) {
      form.patchValue(data)
    }

    return form
  }

  get formBuilder() {
    return this.fb
  }

  private createFormGroup<T = any>(model: T): FormGroup {
    const group = {}
    Object.keys(model).forEach((k) => {
      const value = model[k]
      if (this.isArray(model[k])) {
        group[k] = value && value.length > 0 ? this.fb.array(value) : this.fb.array([])
      } else {
        group[k] = model[k] === undefined || !this.isObject(model[k]) ? [value] : this.createFormGroup(value)
      }
    })

    return this.fb.group(group)
  }

  /* Método para realizar validação manual de um FormGoup */
  validateAndNotify(formGroup: FormGroup, validator: ValidatorFunction<any>) {
    this.touchAll(formGroup)
    this.notifyErrors(formGroup, validator(formGroup.getRawValue()))
  }

  notifyErrors(formGroup: FormGroup, errors: any) {
    this.clearErrors(formGroup)

    if (errors) {
      const dotObj = dot(errors)
      Object.keys(dotObj).forEach((key) => {
        const control = formGroup.get(this.dotKeyToArrayKey(key))
        if (control?.touched || control?.dirty) {
          control.setErrors({ error: dotObj[key] })
        }
      })
    }

    formGroup.updateValueAndValidity()
  }

  private dotKeyToArrayKey(dotKet: string) {
    return dotKet?.replace(/\[/g, '.').replace(/\]/g, '')
  }

  private clearErrors(parent: any) {
    if (parent?.controls) {
      Object.keys(parent.controls).forEach((key) => {
        this.clearErrors(parent.get(key))
      })
    }
    parent?.setErrors(null)
  }

  touchAll(formGroup: any) {
    const controls = formGroup?.controls
    if (controls) {
      Object.keys(controls).forEach((field) => {
        const control = formGroup.get(field)
        this.touchAll(control)
      })
    }

    formGroup.markAsTouched({ onlySelf: true })
  }

  resetForm(formGroup: FormGroup) {
    formGroup.reset()
  }

  convertToControl(control: AbstractControl) {
    const fullPath = this.getControlPath(control)
    const name = fullPath.substr(fullPath.lastIndexOf('.') + 1)
    const parent = control.parent as FormGroup
    if (name) {
      parent.setControl(name, new FormControl(control.value))
    }
  }

  getControlName(c: AbstractControl): string | null {
    if (!c.parent) return null
    const formGroup = c.parent.controls
    return Object.keys(formGroup).find((name) => c === formGroup[name]) || null
  }

  getControlPath(c: AbstractControl, path: string = ''): string | null {
    const newPath = this.getControlName(c) + path
    return c.parent && this.getControlName(c.parent) ? this.getControlPath(c.parent, '.' + newPath) : newPath
  }

  disableControls(formGroup: FormGroup, controlNames: string[]) {
    controlNames.forEach((control) => {
      formGroup.get(control).disable()
    })
  }

  disableAllControls(parent: any) {
    if (parent?.controls) {
      Object.keys(parent.controls).forEach((key) => {
        this.disableAllControls(parent.get(key))
      })
    }
    parent?.disable()
  }

  private isArray(obj: any): boolean {
    return Object.prototype.toString.call(obj) === '[object Array]'
  }

  private isObject(obj: any): boolean {
    return Object.prototype.toString.call(obj) === '[object Object]'
  }
}
